import type { Dayjs, ManipulateType, OpUnitType } from 'dayjs';
import { useI18n } from 'vue-i18n';
import dayjs from '@/dayjs';
import { useSettings } from './useSettings';

const { getSetting } = useSettings();

export function useDate() {
  const { d } = useI18n();

  const getAsDate = (date: NullableDateOptionType = undefined): Date => dayjs(date).toDate();

  const getIsoWeekday = (date: NullableDateOptionType = undefined): number => dayjs(date).isoWeekday();
  const startOfToday = (): Dayjs => dayjs().startOf('day');
  const endOfToday = (): Dayjs => dayjs().endOf('day');
  const startOfTomorrow = (): Dayjs => dayjs().add(1, 'day').startOf('day');
  const endOfTomorrow = (): Dayjs => dayjs().add(1, 'day').endOf('day');

  const currentUnix = (): number => dayjs().unix();
  const unixFilter = (timestamp: number): boolean => currentUnix() - timestamp < 1800;

  const formatDate = (date: NullableDateOptionType, format: string | undefined = 'YYYY-MM-DD'): string => dayjs(date).format(format);
  // sat
  const toShortDay = (date: NullableDateOptionType): string => d(getAsDate(date), 'short_day');

  // 03 Feb
  const toShortDate = (date: NullableDateOptionType): string => d(getAsDate(date), 'short');

  // Tue, 03 Feb
  const toShortDateWithDay = (date: NullableDateOptionType): string => d(getAsDate(date), 'short_with_day');

  // 2024-01-17 21:13:31
  const toDateTime = (date: NullableDateOptionType): string => dayjs(date).format('YYYY-MM-DD HH:mm:ss');

  // Wednesday, January 17th 2024
  function toLongDateWithDay(date: NullableDateOptionType): string {
    return d(getAsDate(date), 'long_date_with_day');
  }

  // Wed, January 17th 2024
  function toLongDateWithShortDay(date: NullableDateOptionType): string {
    return d(getAsDate(date), 'long_date_with_short_day');
  }

  // Wednesday, January 17th 2024 08:00am
  const toLongDateWithDayAndTime = (date: NullableDateOptionType): string => d(getAsDate(date), 'long_date_with_day_and_time');

  // January 17th 2024 - 08:00
  const toLongDateWithTime = (date: NullableDateOptionType): string => d(getAsDate(date), 'long_date_with_time');

  // January 17th 2024
  const toLongDate = (date: NullableDateOptionType): string => d(getAsDate(date), 'long');

  // YYYY-MM-DD
  const toUniversalDate = (date: NullableDateOptionType): string => dayjs(date).format('YYYY-MM-DD');

  // YYYY-MM-DD HH:mm:ss
  const parseDateAndTime = (date: string, time: string): Date => dayjs(`${date} ${time}`).toDate();

  // Wednesday
  const toLongDay = (date: NullableDateOptionType): string => d(getAsDate(date), 'long_day');

  // Wed Jan 17 2024 23:59:59 GMT+0000
  // Return end of today if no parameters passed.
  const endOf = (date: NullableDateOptionType, unit: OpUnitType = 'day'): Dayjs => dayjs(date).endOf(unit);

  // Wed Jan 17 2024 00:00:00 GMT+0000
  // Return start of today if no parameters passed.
  const startOf = (date: NullableDateOptionType, unit: OpUnitType = 'day'): Dayjs => dayjs(date).startOf(unit);

  // 1
  const daysDiffFromToday = (date: NullableDateOptionType): number => dayjs(date).endOf('day').diff(endOfToday(), 'days');

  // 8:00am
  const toShortTime = (date: NullableDateOptionType, inputFormat: string = 'HH:mm:ss'): string => dayjs(date, inputFormat).format('h:mma');

  // 2024
  const thisYear = (): number => dayjs().year();

  // As Date -> Max Current Needs Date
  const maxCurrentNeedsDate = (): Date => dayjs().add(getSetting('number_of_day_available_for_current_need_page') ?? 0, 'days').startOf('day').toDate();

  const isSameOrBefore = (
    date1: DateOptionType,
    date2: NullableDateOptionType = undefined,
  ): boolean => dayjs(date1).isSameOrBefore(dayjs(date2));

  const isSameOrAfter = (
    date1: DateOptionType,
    date2: NullableDateOptionType = undefined,
  ): boolean => dayjs(date1).isSameOrAfter(dayjs(date2));

  const isAfterToday = (date: DateOptionType): boolean => dayjs().isBefore(date);

  const isBeforeToday = (date: DateOptionType): boolean => dayjs().isAfter(date);

  const getToday = (): Dayjs => dayjs();

  const getAsValidDateOrNull = (date: NullableDateOptionType): Date | null => {
    const parsedDate = dayjs(date);
    return parsedDate.isValid()
      ? parsedDate.toDate()
      : null;
  };

  const addToDate = (
    date: NullableDateOptionType,
    number: number = 1,
    unit: ManipulateType = 'days',
  ): Dayjs => dayjs(date).add(number, unit);

  const subtractFromDate = (
    date: DateOptionType | undefined,
    number: number = 1,
    unit: ManipulateType = 'days',
  ): Dayjs => dayjs(date).subtract(number, unit);

  const isInFutureByAtLeast = (
    date: DateOptionType,
    number: number = 7,
    unit: OpUnitType = 'days',
  ): boolean => dayjs().diff(dayjs(date), unit) < number;

  const fromNow = (date: DateOptionType): string => dayjs(date).fromNow();

  // Day.js durations do not parse HH:mm:ss, so the parts are added one by one.
  const calculateAndFormatEndTime = (time: string, duration: string): string => {
    const [hours = 0, minutes = 0, seconds = 0] = duration.split(':').map(Number);
    return dayjs(time, 'HH:mm:ss')
      .add(hours, 'hour')
      .add(minutes, 'minute')
      .add(seconds, 'second')
      .format('h:mma');
  };

  const getTimeDiff = (start: string, end: string, inputFormat: string = 'HH:mm', unit: OpUnitType = 'minutes'): number => {
    const startTime = dayjs(start, inputFormat);
    const endTime = dayjs(end, inputFormat);
    const diff = endTime.diff(startTime, unit);
    return Math.abs(diff);
  };

  const timeSince = (date: DateOptionType): number => dayjs().diff(date, 'years');

  const isoWeekday = (date: DateOptionType): number => dayjs(date).isoWeekday();

  const isTodayOrTomorrow = (date: DateOptionType): boolean => dayjs(date).isSameOrAfter(startOfToday()) && dayjs(date).isSameOrBefore(endOfTomorrow());

  const isTomorrow = (date: NullableDateOptionType): boolean => {
    const localDate = dayjs(date);
    return localDate.isSameOrAfter(startOfTomorrow()) && localDate.isSameOrBefore(endOfTomorrow());
  };

  const isToday = (date: NullableDateOptionType): boolean => {
    if (date === undefined) {
      return false;
    }
    const localDate = dayjs(date);

    return localDate.isSameOrAfter(startOfToday()) && localDate.isSameOrBefore(endOfToday());
  };

  // getSetting maps an unset setting to null; both are used in arithmetic, so
  // they stand in as zero exactly as null did before being typed.
  const contactsVisibleForDays: number = getSetting('shift_contacts_visible_for_days') ?? 0;
  const planningWeeks: number = getSetting('planning_weeks') ?? 0;

  const contactsVisible = (date: string): boolean => {
    const shiftDate = getAsDate(`${date}T00:00`);
    const maxDate = getAsDate();
    maxDate.setDate(maxDate.getDate() + contactsVisibleForDays);
    return shiftDate < maxDate;
  };

  const planningDates = (): { start: Date; end: Date } => {
    const startDate = getAsDate();
    const endDate = addToDate(undefined, planningWeeks, 'weeks');
    return {
      start: startDate,
      end: endDate.toDate(),
    };
  };

  return {
    addToDate,
    calculateAndFormatEndTime,
    contactsVisible,
    daysDiffFromToday,
    endOf,
    fromNow,
    getAsDate,
    getAsValidDateOrNull,
    getTimeDiff,
    getToday,
    isAfterToday,
    isBeforeToday,
    isoWeekday,
    isSameOrAfter,
    isSameOrBefore,
    isTodayOrTomorrow,
    isToday,
    isTomorrow,
    maxCurrentNeedsDate,
    isInFutureByAtLeast,
    planningDates,
    startOf,
    subtractFromDate,
    thisYear,
    toDateTime,
    toLongDateWithDayAndTime,
    toLongDateWithDay,
    toLongDateWithTime,
    toLongDate,
    toLongDay,
    toShortDate,
    toShortDay,
    toShortTime,
    parseDateAndTime,
    toUniversalDate,
    unixFilter,
    currentUnix,
    toLongDateWithShortDay,
    formatDate,
    contactsVisibleForDays,
    timeSince,
    toShortDateWithDay,
    getIsoWeekday,
  };
}
